import './App.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import theme from './muiTheme';
import CssBaseline from '@mui/material/CssBaseline';
import { configContext, useGetAppConfig } from './hooks/useAppConfig';
import { SnackbarProvider } from 'notistack';
import { HashRouter as Router } from 'react-router-dom';
import { ProvideAuth } from '@timmons-group/shared-react-components/hooks';
import { LineLoader, ContainerWithCard } from '@timmons-group/shared-react-components';
import { ProvideRecipient } from './hooks/useRecipient';
import { ProvideOrgRecipient } from './hooks/useOrgRecipient';
import { ProvideUnit } from './hooks/useUnit';
import { ProvidePractice } from 'hooks/usePractice';

import AppRoutes from './AppRoutes';
import { ProvideActivity } from 'hooks/useActivity';

const ThemeAndRoutes = () => {
  // useGetAppConfig will immediately attempt to load the tenant specific application config on mount
  const appConfig = useGetAppConfig();

  // Dont show the app unless the config is loaded
  if (appConfig.isLoaded) {
    return (
      // appConfig is provided to all children use hook useConfig to consume
      <configContext.Provider value={appConfig?.config}>
        {/*
          theme is passed down to all children
          it can be accessed via the useTheme hook provided by the mui/material
        */}
        <React.StrictMode>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Router>
                <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                  <ProvideRecipient>
                    <ProvideOrgRecipient>
                      <ProvideUnit>
                        <ProvidePractice>
                          <ProvideActivity>
                            <AppRoutes />
                          </ProvideActivity>
                        </ProvidePractice>
                      </ProvideUnit>
                    </ProvideOrgRecipient>
                  </ProvideRecipient>
                </SnackbarProvider>
              </Router>
            </LocalizationProvider>
          </ThemeProvider>
        </React.StrictMode>
      </configContext.Provider>
    );
  }
  else {
    return (
      <ContainerWithCard>
        <LineLoader color="primary" message="Loading Application theme and configuration..." />
      </ContainerWithCard>
    );
  }
}

const App = () => {
  const [config, setConfig] = useState(null);

  // fetch a config with useEffect
  useEffect(() => {
    if (!config) {
      axios.get('/api/oauth/config').then(res => {
        setConfig(res.data);
      }).catch(err => {
        console.log('err', err);
        setConfig({ error: 'Config not found' });
      });
    }
  }, []);

  if (!config) {
    return (
      <ContainerWithCard>
        <LineLoader message="Initializing..." color="secondary" />
      </ContainerWithCard >
    );
  }

  // Add context Auth provider to the App
  return (
    <ProvideAuth config={config}>
      <ThemeAndRoutes />
    </ProvideAuth>
  );
}

export default App;
